import { useState } from "react";
import { Check, ChevronDown, Plus, Settings2 } from "lucide-react";
import { useNavigate } from "react-router";
import { Button } from "@/shared/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/components/ui/DropdownMenu";
import { useLists } from "@/shared/lib/lists";
import { useActiveScope, withListParam } from "@/shared/lib/scope";
import { cn } from "@/shared/lib/utils";
import { ManageListsDialog } from "./ManageListsDialog";
import { NewListDialog } from "./NewListDialog";

const ALL_LABEL = "All Contacts";

interface ListScopeNavProps {
  onNavigate?: () => void;
  className?: string;
}

/**
 * Header control for the active list scope. Renders three siblings:
 *   1. A pill DropdownMenu that lists "All Contacts" + the user's lists
 *      and navigates to /app/contacts?list=<id> on select.
 *   2. A "New list" icon button.
 *   3. A "Manage lists" icon button.
 *
 * The icon buttons live next to the chip (rather than as menu items at
 * the bottom of the dropdown) so list management is one click away from
 * the header instead of two.
 */
export function ListScopeNav({ onNavigate, className }: ListScopeNavProps) {
  const navigate = useNavigate();
  const lists = useLists();
  const { listId: activeId, list: active } = useActiveScope();
  const [newOpen, setNewOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);

  const items = lists.data?.items ?? [];
  const triggerLabel = active?.name ?? ALL_LABEL;

  function selectList(id: string | null) {
    onNavigate?.();
    navigate(withListParam("/app/contacts", id));
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Switch contacts scope"
          className={cn(
            // Grow to fill an explicit parent width (mobile dock passes
            // `w-full` to ListScopeNav). On desktop the parent collapses
            // to content, so `flex-1` is a no-op and the cap applies.
            "inline-flex min-w-0 flex-1 items-center justify-center gap-2 lg:max-w-[14rem]",
            "rounded-full px-6 py-2.5",
            "type-label-base tracking-wider uppercase",
            "glass-surface-subtle text-text",
            "transition-colors duration-150 hover:bg-white/60",
            "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand",
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-text-muted" />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="center"
          className="min-w-[var(--radix-dropdown-menu-trigger-width)] lg:min-w-[14rem]"
        >
          <DropdownMenuItem onSelect={() => selectList(null)}>
            <Check
              aria-hidden
              className={cn(
                "h-4 w-4 text-text-muted",
                activeId ? "opacity-0" : "opacity-100",
              )}
            />
            <span>{ALL_LABEL}</span>
          </DropdownMenuItem>

          {items.map((list) => (
            <DropdownMenuItem
              key={list.id}
              onSelect={() => selectList(list.id)}
            >
              <Check
                aria-hidden
                className={cn(
                  "h-4 w-4 text-text-muted",
                  activeId === list.id ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="flex-1 truncate">{list.name}</span>
              <span className="type-body-sm text-text-muted">
                {list.memberCount}
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        variant="secondary"
        size="icon"
        aria-label="New list"
        onClick={() => setNewOpen(true)}
      >
        <Plus aria-hidden className="h-4 w-4" />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        aria-label="Manage lists"
        onClick={() => setManageOpen(true)}
      >
        <Settings2 aria-hidden className="h-4 w-4" />
      </Button>

      <NewListDialog open={newOpen} onOpenChange={setNewOpen} />
      <ManageListsDialog
        open={manageOpen}
        onOpenChange={setManageOpen}
        onCreateNew={() => setNewOpen(true)}
      />
    </div>
  );
}
